// src/app/core/services/balance-validator.service.ts
import { Injectable, Inject, Optional } from '@angular/core';

export interface TokenLite {
  symbol: string;
  address: string; // '0x0000...' pour le natif
  chainId: number;
  decimals?: number;
}

export interface BridgeCheckArgs {
  takerAddress?: string | null;
  fromChainId: number;
  sellTokenSymbol: string;
  sellAmount: string | number;     // ex: "1.25"
  fullGasWei: string | number | bigint; // ✅ bigint accepté
  availableSellTokens: TokenLite[]; // tokens sur la chaîne source
  sellTokenBalances: Record<string, string | number>; // clés: "address_chainId" ET/OU "SYMBOL"
  // Renvoie le symbole natif de la chaîne (ex: ETH, POL, BNB…)
  getNativeTokenSymbol: (chainId: number) => string;
  marginPct?: number; // défaut 0.05
}

export interface SwapCheckArgs {
  takerAddress?: string | null;
  chainId: number;
  sellTokenSymbol: string;
  sellAmount: string | number;
  fullGasWei: string | number | bigint;          // accepte bigint
  availableTokens: TokenLite[];                  // tokens sur la chaîne
  tokenBalances: Record<string, string | number>;
  getNativeTokenSymbol: (chainId: number) => string;
  marginPct?: number;                            // défaut = injecté ou 0.05
}

export interface SwapCheckResult {
  disabled: boolean;
  reasons: string[]; // WALLET_DISCONNECTED | INVALID_AMOUNT | INSUFFICIENT_NATIVE_GAS | INSUFFICIENT_SELL_TOKEN
  numbers: {
    nativeBalance: number;
    sellBalance: number;
    amount: number;
    gasCostNative: number;
    nativeNeeded: number; // gas*(1+marge) [+ amount si on vend le natif]
    sellingNative: boolean;
    marginPct: number;
  };
}

export interface BridgeCheckResult {
  disabled: boolean;
  reasons: string[]; // ex: ["WALLET_DISCONNECTED", "INSUFFICIENT_NATIVE_GAS", ...]
  numbers: {
    nativeBalance: number;
    sellBalance: number;
    nativeNeeded: number;
    gasCostNative: number;
    amount: number;
    sellingNative: boolean;
    marginPct: number;
  };
}

const ZERO = '0x0000000000000000000000000000000000000000';
export const BALANCE_VALIDATOR_DEFAULT_MARGIN = 0.05;

/** ──────────────────────────────────────────────────────────────
 *  Canon natif + alias (gère MATIC ⇄ POL sur Polygon 137)
 *  ───────────────────────────────────────────────────────────── */
const NATIVE_SYMBOL_MAP: Record<number, string> = {
  1: 'ETH',
  137: 'POL',  // ✅ Polygon
  56: 'BNB',
  42161: 'ETH'
};

// Aliases tolérés par chaîne (pour rétro-compat : MATIC encore utilisé)
const NATIVE_ALIASES: Record<number, string[]> = {
  137: ['POL', 'MATIC']
};

// Canonise un symbole natif selon la chaîne (ex: MATIC -> POL sur 137)
function toCanonicalNativeSymbol(chainId: number, symbol: string): string {
  const s = (symbol || '').toUpperCase();
  const canon = NATIVE_SYMBOL_MAP[chainId];
  if (!canon) return s;
  const aliases = new Set([canon, ...(NATIVE_ALIASES[chainId] ?? [])]);
  return aliases.has(s) ? canon : s;
}

// Égalité “intelligente” (gère MATIC == POL sur 137)
function nativeSymbolsEqual(a: string, b: string, chainId: number): boolean {
  return toCanonicalNativeSymbol(chainId, a) === toCanonicalNativeSymbol(chainId, b);
}

@Injectable({ providedIn: 'root' })
export class BalanceValidatorService {
  constructor(
    @Optional() @Inject('BALANCE_VALIDATOR_MARGIN') private defaultMargin?: number
  ) { }

  isBridgeDisabled(args: BridgeCheckArgs): BridgeCheckResult {
    const {
      takerAddress,
      fromChainId,
      sellTokenSymbol,
      sellAmount,
      fullGasWei,
      availableSellTokens,
      sellTokenBalances,
      getNativeTokenSymbol,
    } = args;

    const marginPct = args.marginPct ?? this.defaultMargin ?? BALANCE_VALIDATOR_DEFAULT_MARGIN;

    const amount = +sellAmount || 0;
    const gasCostNative = Number(fullGasWei.toString()) / 1e18;

    // ✅ sellingNative robuste (POL/MATIC ok)
    const sellingNative = nativeSymbolsEqual(
      getNativeTokenSymbol(fromChainId),
      sellTokenSymbol,
      fromChainId
    );

    const nativeBalance = this.getNativeBalanceNumber(fromChainId, availableSellTokens, sellTokenBalances);
    const sellBalance = this.getSellBalanceNumber(sellTokenSymbol, fromChainId, availableSellTokens, sellTokenBalances);

    const nativeNeeded = gasCostNative * (1 + marginPct) + (sellingNative ? amount : 0);

    const reasons: string[] = [];
    if (!takerAddress) reasons.push('WALLET_DISCONNECTED');
    if (amount <= 0) reasons.push('INVALID_AMOUNT');
    if (nativeBalance < nativeNeeded) reasons.push('INSUFFICIENT_NATIVE_GAS');
    if (!sellingNative && sellBalance < amount) reasons.push('INSUFFICIENT_SELL_TOKEN');

    return {
      disabled: reasons.length > 0,
      reasons,
      numbers: {
        nativeBalance,
        sellBalance,
        nativeNeeded,
        gasCostNative,
        amount,
        sellingNative,
        marginPct
      }
    };
  }

  isSwapDisabled(args: SwapCheckArgs): SwapCheckResult {
    const {
      takerAddress,
      chainId,
      sellTokenSymbol,
      sellAmount,
      fullGasWei,
      availableTokens,
      tokenBalances,
      getNativeTokenSymbol,
    } = args;

    const marginPct = args.marginPct ?? this.defaultMargin ?? BALANCE_VALIDATOR_DEFAULT_MARGIN;

    const amount = +sellAmount || 0;
    const gasCostNative = Number(fullGasWei.toString()) / 1e18;

    // ✅ sellingNative robuste (POL/MATIC ok)
    const sellingNative = nativeSymbolsEqual(
      getNativeTokenSymbol(chainId),
      sellTokenSymbol,
      chainId
    );

    const nativeBalance = this.getNativeBalanceNumber(chainId, availableTokens, tokenBalances);
    const sellBalance = this.getSellBalanceNumber(sellTokenSymbol, chainId, availableTokens, tokenBalances);

    const nativeNeeded = gasCostNative * (1 + marginPct) + (sellingNative ? amount : 0);

    const reasons: string[] = [];
    if (!takerAddress) reasons.push('WALLET_DISCONNECTED');
    if (amount <= 0) reasons.push('INVALID_AMOUNT');
    if (nativeBalance < nativeNeeded) reasons.push('INSUFFICIENT_NATIVE_GAS');
    if (!sellingNative && sellBalance < amount) reasons.push('INSUFFICIENT_SELL_TOKEN');

    return {
      disabled: reasons.length > 0,
      reasons,
      numbers: {
        nativeBalance,
        sellBalance,
        amount,
        gasCostNative,
        nativeNeeded,
        sellingNative,
        marginPct
      }
    };
  }

  // ---------- Helpers privés ----------

  private getNativeBalanceNumber(
    chainId: number,
    tokens: TokenLite[],
    balances: Record<string, string | number>
  ): number {
    const native = tokens.find(t => t.chainId === chainId && t.address?.toLowerCase() === ZERO);
    if (!native) return 0;

    // clé préférée par adresse_chainId
    const key1 = `${native.address.toLowerCase()}_${native.chainId}`;
    let v: any = balances[key1];

    // ✅ fallback symbol canonique + alias (gère 'POL' et 'MATIC')
    if (v == null) {
      const canon = toCanonicalNativeSymbol(chainId, native.symbol);
      v = balances[canon];
    }
    if (v == null) {
      const aliases = NATIVE_ALIASES[chainId] ?? [];
      for (const s of aliases) {
        if (balances[s] != null) { v = balances[s]; break; }
      }
    }

    v = v ?? '0';
    return parseFloat(String(v || '0'));
  }

  private getSellBalanceNumber(
    symbol: string,
    chainId: number,
    tokens: TokenLite[],
    balances: Record<string, string | number>
  ): number {
    // On tolère aussi les alias pour retrouver le TokenLite (utile si UI passe "MATIC")
    const wantedCanon = toCanonicalNativeSymbol(chainId, symbol);
    const symbolU = (symbol || '').toUpperCase();

    let t =
      tokens.find(tt => (tt.symbol?.toUpperCase?.() === symbolU && tt.chainId === chainId)) ||
      tokens.find(tt => (toCanonicalNativeSymbol(chainId, tt.symbol) === wantedCanon && tt.chainId === chainId));

    if (!t) return 0;

    const addr = (t.address || '').toLowerCase();
    const key1 = `${addr}_${t.chainId}`;

    // clé par adresse prioritaire
    let v: any = balances[key1];

    // ✅ fallback symbol direct + canon + alias (si le token est le natif)
    if (v == null) {
      v = balances[symbolU];
      if (v == null) {
        v = balances[wantedCanon];
      }
      if (v == null && addr === ZERO) {
        const aliases = NATIVE_ALIASES[chainId] ?? [];
        for (const s of aliases) {
          if (balances[s] != null) { v = balances[s]; break; }
        }
      }
    }

    v = v ?? '0';
    return parseFloat(String(v || '0'));
  }
}
